import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CalendarModule } from 'primeng/calendar';
import { CommonModule, DatePipe } from '@angular/common';
import { TimeZoneResponse } from '../../models/peer/timeZoneResponse';
import { UtilityService } from '../../services/utility/utility.service';
import { DropdownModule } from 'primeng/dropdown';
import { DayOfWeek } from '../../models/enums/dayOfWeek';
import { PeerTimingsService } from '../../services/peer-timings/peer-timings.service';
import { AddPeerTimingRequest } from '../../models/peer/addPeerTimingRequest';
import { MessageService } from 'primeng/api';
import { UserStateModel } from '../../states/user/user-state-model';
import { UserSelectors } from '../../states/user/user-selector';
import { Store } from '@ngxs/store';
import { GetAllPeerTimingResponse, PeerTimingDayGroup } from '../../models/peer/getAllPeerTimingResponse';
import { of, Subject, switchMap, takeUntil } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { PeerReviewService } from '../../services/peer-review/peer-review.service';


@Component({
  selector: 'app-peer-timings',
  standalone: true,
  imports: [TableModule,ButtonModule,DialogModule,CalendarModule,FormsModule, CommonModule, DropdownModule,ReactiveFormsModule],
  templateUrl: './peer-timings.component.html',
  styleUrl: './peer-timings.component.scss',
  providers: [DatePipe]
})
export class PeerTimingsComponent implements OnInit{

  visible: boolean = false;
  submitted: boolean = false;
  selectedTimeZone: string = '';
  selectedDay!: DayOfWeek;
  timeZonesList: TimeZoneResponse[] = [];
  newTimeSlotForm!: FormGroup;

  peerReviewer!: any;
  peerReviewerId: string | undefined; //used if landing through admin portal
  allPeerTimings: GetAllPeerTimingResponse | undefined;
  tableData: PeerTimingDayGroup[] = [];
  isLoading: boolean = false;
  private ngUnsubscribeLoadPeerData = new Subject<void>(); 
  private ngUnsubscribeLoadPeerDataByRouteId = new Subject<void>();
  
  constructor(private utilityService: UtilityService,
    private formBuilder: FormBuilder,
    private peerTimingsService: PeerTimingsService,
    private peerReviewService: PeerReviewService,
    private messageService: MessageService,
    private store: Store,
    private datepipe: DatePipe,
    private route: ActivatedRoute
  ) {}

  initializeForm(){
    this.newTimeSlotForm = this.formBuilder.group({
      timeZone: ['', Validators.required],
      from: ['', Validators.required],
      to: ['',Validators.required],
    })
  }

  private loadTimeZones(): void {
    this.utilityService.getTimeZones().subscribe({
      next: (response) => {
        if (response?.result) {
          this.timeZonesList = response.result;
        }
      },
      error: (err) => console.warn('Error loading timezones:', err),
    });
  }

  getDayOfWeek(dayIndex: DayOfWeek): string {
    return DayOfWeek[dayIndex];
  }

  ngOnInit(): void {
    this.peerReviewerId = this.route.snapshot.paramMap.get('id') || '';
    this.initializeForm();
    
    if(this.peerReviewerId){
    this.loadPeerDataByRouteId();
    }else{
      this.loadPeerData();
    }
  }


  loadPeerData() {
    this.isLoading = true;
    this.store.select(UserSelectors.getUser).pipe(
      switchMap(user => {
        if (!user || !user.id) {
          return of(null);
        }
        this.peerReviewer = user;
        return this.peerTimingsService.getAllPeerTimings(user.id);
      }),
      takeUntil(this.ngUnsubscribeLoadPeerData)
    ).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response !== null) {
          this.allPeerTimings = response?.result ?? undefined;
          // Normalize the data to ensure all 7 days are present
          const receivedDays = response?.result?.availabilityByDay ?? [];
          const normalizedDays: PeerTimingDayGroup[] = [];
          //This populates tableData with 7 days no matter the backend response
          for (let day = 0; day < 7; day++) {
            const match = receivedDays.find(d => d.day === day);
            normalizedDays.push({
              day: day,
              availability: match?.availability ?? []
            });
          }
          this.tableData = normalizedDays;
          console.log('Normalized Peer Timings:', this.tableData);
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.warn('Error fetching peer data and timings:', error);
      }
    });
  }

  loadPeerDataByRouteId(){
    this.isLoading = true;
    this.peerReviewService.getPeerReviewerById(this.peerReviewerId).pipe(
      switchMap(user => {
        if (!user || !user.id) {
          return of(null);
        }
        this.peerReviewer = user;
        return this.peerTimingsService.getAllPeerTimings(user.applicationUserId);
      }),
      takeUntil(this.ngUnsubscribeLoadPeerDataByRouteId)
    ).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response !== null) {
          this.allPeerTimings = response?.result ?? undefined;
          const receivedDays = response?.result?.availabilityByDay ?? [];
          const normalizedDays: PeerTimingDayGroup[] = [];
          for (let day = 0; day < 7; day++) {
            const match = receivedDays.find(d => d.day === day);
            normalizedDays.push({
              day: day,
              availability: match?.availability ?? []
            });
          }
          this.tableData = normalizedDays;
          console.log('Normalized Peer Timings:', this.tableData);
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.warn('Error fetching peer data and timings:', error);
      }
    });
  
  }
  
  ngOnDestroy(): void {
    this.ngUnsubscribeLoadPeerData.next();
    this.ngUnsubscribeLoadPeerData.complete();

    this.ngUnsubscribeLoadPeerDataByRouteId.next();
    this.ngUnsubscribeLoadPeerDataByRouteId.complete();
  }

  deleteSingleTimeSlot(dayIndex: number, slotIndex: number){
    this.isLoading = true;
    const dayGroup = this.tableData[dayIndex];
    const timeSlot = dayGroup.availability[slotIndex];
    if(dayGroup && dayGroup.availability && dayGroup.availability[slotIndex]){
      if(timeSlot.id){
        this.peerTimingsService.deletePeerTiming(timeSlot.id).subscribe({
          next: (response) => {
            this.isLoading = false;
            // Remove the time slot from the local array
            dayGroup.availability.splice(slotIndex, 1);
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Time slot deleted successfully' });
          },
          error: (err) => {
            this.isLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Not Able to delete time slot' });
            console.warn('Error deleting time slot:', err);
          }
        });
      } else {
        console.warn('Time slot ID is missing.');
      }
    }
  }
 
  saveSlot(dayIndex: number) {
    if (this.newTimeSlotForm.invalid) {
      this.messageService.add({severity: 'error',summary: 'Error',detail: 'Please fill all required fields'});
      return;
    }

    const formdata = this.newTimeSlotForm.value;
    const fromTime = this.datepipe.transform(formdata.from, 'hh:mm a') ?? '';
    const toTime = this.datepipe.transform(formdata.to, 'hh:mm a') ?? '';
    let applicationUserId: string | undefined;

    if (!fromTime || !toTime) {
      this.messageService.add({severity: 'error',summary: 'Error',detail: 'Invalid time values'});
      return;
    }

    if (this.peerReviewerId) {
      applicationUserId = this.peerReviewer.applicationUserId;
    } else {
      applicationUserId = this.peerReviewer.id;
    }

    const timeSlotData: AddPeerTimingRequest = {
      applicationUserId: applicationUserId!,
      timezone: formdata.timeZone,
      dayOfWeek: dayIndex,
      from: fromTime,
      to: toTime,
    };

    const dayGroup:PeerTimingDayGroup = this.tableData[dayIndex]??0;

    if((timeSlotData.from >= timeSlotData.to)||
      (timeSlotData.from === timeSlotData.to)){
      this.messageService.add({severity: 'error',summary: 'Error',detail: 'Invalid time range'});
      return;
    }

    // Check for overlap 
    for (let i = 0; i < dayGroup.availability.length; i++) {
      const slot = dayGroup.availability[i];
      if (
        (timeSlotData.from >= slot.from && timeSlotData.from < slot.to) ||
        (timeSlotData.to > slot.from && timeSlotData.to <= slot.to) ||
        (timeSlotData.from <= slot.from && timeSlotData.to >= slot.to)
      ) {
        this.messageService.add({severity: 'error',summary: 'Error',detail: 'Time slot overlaps with an existing slot'});
        return;
      }
    }
    this.peerTimingsService.addPeerTiming(timeSlotData).subscribe({
      next: (response) => {
        this.visible = false;
        this.messageService.add({ severity: 'success', summary: 'Success',detail: 'Peer timeslot added successfully'});
        window.location.reload();
      },
      error: (err) => {
        console.warn('Error adding peer timing:', err);
        this.messageService.add({severity: 'error',summary: 'Error',detail: 'Failed to add timeslot'});
      },
    });
  }

  showDialog(id: string, day: DayOfWeek) {
    this.newTimeSlotForm.reset();
    this.loadTimeZones();
    this.selectedDay = day;
    this.visible = true;
  }

}
